import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import AttachmentIcon from '../icons/AttachmentIcon';
import { i18nService } from '../../services/i18n';
import {
  formatCoworkFileSize,
  getCoworkFileMeta,
  isCoworkImagePath,
} from './coworkAttachmentPresentation';

interface Props {
  path: string;
  name: string;
  thumbnail?: string;
  fileSize?: number;
  compact: boolean;
  readOnly?: boolean;
  onPreview?: (path: string) => void;
  onRemove?: (path: string) => void;
}

/**
 * Cowork 通用附件卡片（移植桌面端 CoworkAttachmentCard）。
 *
 * @remarks 输入框附件区使用；只读模式仅隐藏删除入口，图片仍可点击预览。
 */
const CoworkAttachmentCard: React.FC<Props> = ({
  path,
  name,
  thumbnail,
  fileSize,
  compact,
  readOnly = false,
  onPreview,
  onRemove,
}) => {
  const isImage = isCoworkImagePath(path) || Boolean(thumbnail);
  const meta = getCoworkFileMeta(isCoworkImagePath(path) ? path : name);
  const sizeText = fileSize !== undefined ? formatCoworkFileSize(fileSize) : '';
  const subtitle = sizeText ? `${meta.label} · ${sizeText}` : meta.label;
  const canPreview = isImage && Boolean(onPreview);

  if (isImage && compact) {
    return (
      <div
        className={`group relative h-16 w-16 ${canPreview ? 'cursor-zoom-in' : ''}`}
        onClick={() => onPreview?.(path)}
        aria-label={name}
      >
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-claude-bg dark:bg-claude-darkBg">
          {thumbnail ? (
            <img src={thumbnail} alt={name} className="h-full w-full object-cover" draggable={false} />
          ) : (
            <AttachmentIcon className="h-4 w-4 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
          )}
        </div>
        {!readOnly && onRemove ? (
          <button
            type="button"
            onClick={(event) => { event.stopPropagation(); onRemove(path); }}
            className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white opacity-0 shadow ring-1 ring-black/10 transition-opacity group-hover:opacity-100 dark:bg-claude-darkSurfaceHover dark:ring-claude-darkBorder/60"
            aria-label={i18nService.t('coworkAttachmentRemove')}
          >
            <XMarkIcon className="h-3 w-3 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`group relative flex w-[220px] items-center gap-2.5 rounded-xl bg-claude-bg p-2 pr-3 transition-colors hover:bg-claude-surfaceHover dark:bg-claude-darkBg dark:hover:bg-claude-darkSurfaceHover ${canPreview ? 'cursor-zoom-in' : 'cursor-default'}`}
      onClick={() => { if (canPreview) onPreview?.(path); }}
      aria-label={`${name}，${subtitle}`}
      title={path}
    >
      <div
        className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg"
        style={isImage && thumbnail ? undefined : { backgroundColor: meta.bg }}
      >
        {isImage && thumbnail ? (
          <img src={thumbnail} alt={name} className="h-full w-full object-cover" draggable={false} />
        ) : (
          <meta.Icon className="h-5 w-5" style={{ color: meta.fg }} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-medium leading-tight text-claude-text dark:text-claude-darkText">
          {name}
        </div>
        <div className="mt-0.5 truncate text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
          {subtitle}
        </div>
      </div>
      {!readOnly && onRemove ? (
        <button
          type="button"
          onClick={(event) => { event.stopPropagation(); onRemove(path); }}
          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white opacity-0 shadow ring-1 ring-black/10 transition-opacity group-hover:opacity-100 dark:bg-claude-darkSurfaceHover dark:ring-claude-darkBorder/60"
          aria-label={i18nService.t('coworkAttachmentRemove')}
        >
          <XMarkIcon className="h-3 w-3 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
        </button>
      ) : null}
    </div>
  );
};

export default CoworkAttachmentCard;
